import React, { useEffect, useState, useMemo } from "react";
import { Modal, Row, Col, notification, Button, Skeleton, Divider } from "antd";
import moment from "moment";
import axios from "axios";

const confirm = Modal.confirm;

const openNotificationWithIcon = (type, message, description) => {
    notification[type]({
        message: message,
        description: description
    });
};

const parseJson = (value, fallback = {}) => {
    if (!value) return fallback;
    if (typeof value === "object" && !Array.isArray(value)) return value;
    try {
        return JSON.parse(value) || fallback;
    } catch {
        return fallback;
    }
};

const ModalPlanoAtendimentoPAEE = ({ id, visible, onCancel, onDelete }) => {
    const [plano, setPlano] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const apresentacao = useMemo(() => parseJson(plano?.apresentacao), [plano?.apresentacao]);
    const descricao = useMemo(() => parseJson(plano?.descricao_aluno), [plano?.descricao_aluno]);
    const objetivos = useMemo(() => parseJson(plano?.objetivos_educacionais), [plano?.objetivos_educacionais]);
    const acoes = useMemo(() => parseJson(plano?.plano_acao), [plano?.plano_acao]);
    const periodo = useMemo(() => parseJson(plano?.periodo_duracao), [plano?.periodo_duracao]);
    const resultados = useMemo(() => parseJson(plano?.resultados), [plano?.resultados]);
    const observacoes = useMemo(() => parseJson(plano?.observacoes_complementares), [plano?.observacoes_complementares]);

    const carregarDetalhe = id => {
        setIsLoading(true);
        axios({
            method: "get",
            url: "/api/plano-atendimento/carregar-plano-atendimento",
            params: { id }
        })
            .then(response => {
                setPlano(response.data);
            })
            .catch(error => {
                openNotificationWithIcon(
                    "error",
                    "Erro",
                    error.response?.data || "Ocorreu um erro ao carregar o detalhe do plano de atendimento educacional especializado."
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const validarExcluir = () => {
        confirm({
            title: "Pretende remover este plano de atendimento educacional especializado?",
            okText: "Confirmar",
            cancelText: "Cancelar",
            onOk: () => excluir()
        });
    };

    const excluir = () => {
        axios({
            method: "put",
            url: "/api/plano-atendimento/excluir-plano-atendimento",
            params: { id }
        })
            .then(() => {
                openNotificationWithIcon(
                    "success",
                    "Plano removido",
                    "Plano de atendimento educacional especializado foi removido com sucesso."
                );
                onDelete();
            })
            .catch(error => {
                openNotificationWithIcon(
                    "error",
                    "Erro",
                    error.response?.data || "Ocorreu um erro ao excluir o plano de atendimento educacional especializado."
                );
            });
    };

    useEffect(() => {
        if (id && visible) carregarDetalhe(id);
    }, [id]);

    // ── Render helpers (mesma lógica do primeiro modal) ──────────────────────

    const renderField = (label, value, span = 12) => (
        <Col span={span} style={{ marginBottom: 12 }}>
            <span className="field-label">{label}</span>
            <Skeleton loading={isLoading} paragraph={false} active className="field-skeleton">
                <p className="field-value" style={{ fontWeight: "bold", marginBottom: 0 }}>
                    {value || "-"}
                </p>
            </Skeleton>
        </Col>
    );

    const renderFieldWide = (label, value) => (
        <Col span={24} style={{ marginBottom: 12 }}>
            <span className="field-label">{label}</span>
            <Skeleton loading={isLoading} title={false} active className="field-skeleton">
                <p className="field-value--wide" style={{ fontWeight: "bold", marginBottom: 0 }}>
                    {value || "-"}
                </p>
            </Skeleton>
        </Col>
    );

    const renderListField = (label, lista = []) => (
        <Col span={24} style={{ marginBottom: 12 }}>
            <span className="field-label">{label}</span>
            <Skeleton loading={isLoading} title={false} active className="field-skeleton">
                {Array.isArray(lista) && lista.length > 0 ? (
                    <ol style={{ paddingLeft: 18, margin: 0, fontWeight: "bold" }}>
                        {lista.map((item, index) => (
                            <li key={index} className="field-value--wide" style={{ marginBottom: 4 }}>
                                {item || "-"}
                            </li>
                        ))}
                    </ol>
                ) : (
                    <p className="field-value--wide" style={{ fontWeight: "bold", marginBottom: 0 }}>
                        -
                    </p>
                )}
            </Skeleton>
        </Col>
    );

    const formatDate = date => (date ? moment(date).format("DD/MM/YYYY") : null);

    return (
        <Modal
            title={
                <div>
                    <div style={{ fontWeight: "bold", fontSize: 16 }}>Plano de Atendimento Educacional Especializado</div>
                    <div style={{ fontSize: 12, color: "#888" }}>Visualização do plano de atendimento</div>
                </div>
            }
            className="modal-detalhe-info"
            visible={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancelar" onClick={onCancel}>
                    Fechar
                </Button>,
                <Button key="eliminar" className="modal-detalhe-info-delete-btn" type="danger" onClick={validarExcluir}>
                    Excluir Plano
                </Button>
            ]}
            centered
            width={800}
            bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
        >
            <div className="modal-detalhe-info__body">
                <div className="modal-detalhe-info__section">
                    {/* Apresentação */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        Apresentação
                    </Divider>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {renderField("Ano letivo", apresentacao.ano_letivo)}
                        {renderField("Aluno(a)", apresentacao.aluno)}
                        {renderField("Data de nascimento", formatDate(apresentacao.data_nascimento))}
                        {renderField("Série / Turma", apresentacao.serie_turma)}
                        {renderField("Professor(a) SRM", apresentacao.professor_srm)}
                        {renderField("Pedagogo(a) / Coordenador(a)", apresentacao.pedagogo_coordenador)}
                        {renderField("Responsáveis legais", apresentacao.responsaveis_legais, 24)}
                    </Row>

                    {/* 1. Descrição do Aluno */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        1. Descrição do Aluno
                    </Divider>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {renderFieldWide("Características cognitivas, socioemocionais e de aprendizagem", descricao.caracteristicas)}
                        {renderFieldWide("Diagnóstico ou necessidades específicas", descricao.diagnostico)}
                    </Row>

                    {/* 2. Objetivos Educacionais */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        2. Objetivos Educacionais
                    </Divider>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {renderListField("Metas de aprendizagem e desenvolvimento", objetivos.metas_aprendizagem)}
                        {renderListField("Aspectos sociais e emocionais a serem desenvolvidos", objetivos.aspectos_sociais)}
                    </Row>

                    {/* 3. Plano de Ação / Atividades */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        3. Plano de Ação / Atividades
                    </Divider>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {renderFieldWide("Estratégias pedagógicas e métodos de ensino", acoes.estrategias_pedagogicas)}
                        {renderFieldWide("Materiais e recursos adaptados utilizados", acoes.materiais_recursos)}
                        {renderListField("Atividades complementares ou adaptadas", acoes.atividades_complementares)}
                    </Row>

                    {/* 4. Período de Duração */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        4. Período de Duração
                    </Divider>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {renderField("Início", formatDate(periodo.data_inicio))}
                        {renderField("Término", formatDate(periodo.data_termino))}
                        {renderField("Revisão prevista", formatDate(periodo.data_revisao))}
                    </Row>

                    {/* 5. Resultados Esperados e Obtidos */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        5. Resultados Esperados e Obtidos
                    </Divider>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {renderListField("Indicadores de aprendizagem e desenvolvimento", resultados.indicadores_aprendizagem)}
                        {renderFieldWide("Avaliação contínua do progresso do estudante", resultados.avaliacao_progresso)}
                    </Row>

                    {/* 6. Observações Complementares */}
                    <Divider orientation="left" style={{ fontWeight: "bold" }}>
                        6. Observações Complementares
                    </Divider>
                    <Row gutter={16} style={{ marginBottom: 12 }}>
                        {renderFieldWide("Informações adicionais relevantes", observacoes.informacoes_adicionais)}
                        {renderFieldWide("Sugestões de ajustes ou adaptações futuras", observacoes.sugestoes_ajustes)}
                    </Row>
                </div>
            </div>
        </Modal>
    );
};

export default ModalPlanoAtendimentoPAEE;
